import {
  faInfoCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faCheckCircle,
  faBell
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import React, { useContext, useState } from "react";
import { useDrag } from "react-dnd";
import styled from "styled-components";
import { GENERIC_COMPONENT } from "../Component";
import { ComponentItemProps, DefaultItem } from "../ComponentTree";
import { DragSpec, IEditableComponent } from "../EditableComponent";
import { NewItem } from "../NewComponents";
import { BarBoolProperty } from "../PropertiesBar";
import { PageContext } from "../../PageContext";
import { 
  AlertSpec, 
  AlertVariant, 
  ALERT_COMPONENT, 
  AlertContainer, 
  IconWrapper, 
  Content, 
  Title,
  getVariantColor,
  getVariantIcon 
} from "./Alert";

const CustomTextarea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 100px;
  display: block;
  position: relative;
  vertical-align: top;
  color: inherit;
  font: inherit;
  resize: vertical;
  background: rgba(255, 255, 255, 0.1);
  outline: none;
  border: 1px dashed #ccc;
  padding: 8px;
  border-radius: 4px;
  margin-top: 5px;
`;

const CustomTitleInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  display: block;
  position: relative;
  vertical-align: top;
  color: inherit;
  font: inherit;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.1);
  outline: none;
  border: 1px dashed #ccc;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 5px;
`;

const SelectContainer = styled.div`
  margin-bottom: 15px;
`;

const SelectLabel = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #adadad;
`;

const SelectInput = styled.select`
  background: #3a4348;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  outline: unset;
  width: calc(100% - 10px);
  :focus {
    box-shadow: #0080ff 0 0 6px 1px;
    outline: unset;
  }
`;

export const EditComponent = observer((props: { spec: AlertSpec }) => {
  const { isEditing } = useContext(PageContext);
  const [isContentEditing, setIsContentEditing] = useState<boolean>(false);
  const [isTitleEditing, setIsTitleEditing] = useState<boolean>(false);
  
  const { variant, title, content, showTitle, showIcon } = props.spec;
  const iconColor = getVariantColor(variant);
  const icon = getVariantIcon(variant);

  return (
    <AlertContainer variant={variant}>
      {showIcon && (
        <IconWrapper color={iconColor}>
          <FontAwesomeIcon icon={icon} size="lg" />
        </IconWrapper>
      )}
      <Content>
        {showTitle && (
          isTitleEditing ? (
            <CustomTitleInput
              value={props.spec.title || ""}
              onChange={(event) => (props.spec.title = event.target.value)}
              onBlur={() => setIsTitleEditing(false)}
              placeholder="Enter title here"
              autoFocus
            />
          ) : (
            <Title 
              onClick={isEditing ? () => setIsTitleEditing(true) : undefined}
              style={isEditing ? { cursor: 'pointer' } : undefined}
            >
              {props.spec.title || "Title (click to edit)"}
            </Title>
          )
        )}
        
        {isContentEditing ? (
          <CustomTextarea
            value={props.spec.content}
            onChange={(event) => (props.spec.content = event.target.value)}
            onBlur={() => setIsContentEditing(false)}
            autoFocus
          />
        ) : (
          <div 
            onClick={isEditing ? () => setIsContentEditing(true) : undefined}
            style={isEditing ? { cursor: 'pointer' } : undefined}
          >
            {props.spec.content || "Click to edit content"}
          </div>
        )}
      </Content>
    </AlertContainer>
  );
});

const Properties = observer((props: { spec: AlertSpec }) => {
  return (
    <div>
      <SelectContainer>
        <SelectLabel>Typ:</SelectLabel>
        <SelectInput
          value={props.spec.variant}
          onChange={(e) => props.spec.variant = e.target.value as AlertVariant}
        >
          <option value="info">Info</option>
          <option value="warning">Warnung</option>
          <option value="danger">Gefahr</option>
          <option value="success">Erfolg</option>
        </SelectInput>
      </SelectContainer>
      
      <BarBoolProperty
        name="Titel anzeigen"
        value={props.spec.showTitle}
        onChange={(newValue) => (props.spec.showTitle = newValue)}
      />
      
      <BarBoolProperty
        name="Icon anzeigen"
        value={props.spec.showIcon}
        onChange={(newValue) => (props.spec.showIcon = newValue)}
      />
    </div>
  );
});

const Item = observer((props: ComponentItemProps<AlertSpec>) => {
  return <DefaultItem {...props} icon={faBell} name="Alert" />;
});

const New = () => {
  const [, drag] = useDrag<DragSpec, unknown, unknown>(() => ({
    type: GENERIC_COMPONENT,
    item: {
      spec: {
        type: ALERT_COMPONENT,
        variant: "info",
        content: "Hier den Text eingeben",
        showTitle: false,
        showIcon: true
      },
    },
  }));
  return (
    <NewItem
      isActive={false}
      ref={(elem) => {
        drag(elem);
      }}
    >
      <FontAwesomeIcon icon={faBell} />
      <span> Alert </span>
    </NewItem>
  );
};

export const AlertComponent: IEditableComponent<AlertSpec> = {
  component: EditComponent,
  properties: Properties,
  item: Item,
  new: New,
};
